import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { label: "Home", href: "#hero" },
  { label: "Services", href: "#services" },
  { label: "Portfolio", href: "/portfolio", isRoute: true },
  { label: "Shop", href: "/shop", isRoute: true },
  { label: "Gallery", href: "#gallery" },
  { label: "Reviews", href: "#reviews" },
  { label: "Book Now", href: "#booking" },
  { label: "Contact", href: "#contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = useCallback((e, link) => {
    e.preventDefault();
    setMobileOpen(false);
    if (link.isRoute) {
      navigate(link.href);
    } else {
      const el = document.querySelector(link.href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [navigate]);

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-brand-bg/90 backdrop-blur-xl border-b border-brand-border/30 shadow-lg shadow-black/20"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        <a
          href="#hero"
          onClick={(e) => handleNavClick(e, { href: "#hero" })}
          className="flex items-center gap-2 group"
          data-testid="logo-link"
          aria-label="Afro Magic Braiding Hair home"
        >
          <img
            src="/logos/v1_emblem.png"
            alt="Afro Magic Braiding Hair logo"
            className="h-10 md:h-12 w-auto object-contain transition-transform group-hover:scale-105"
          />
          <span className="sr-only">Afro Magic Braiding Hair</span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link)}
              data-testid={`nav-${link.isRoute ? link.href.slice(1) : link.href.slice(1)}`}
              className={`text-sm font-sans tracking-wide transition-colors duration-200 ${
                link.label === "Book Now"
                  ? "bg-brand-gold text-black px-5 py-2 font-semibold hover:bg-brand-gold-light"
                  : "text-brand-text-muted hover:text-brand-gold"
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Mobile Toggle */}
        <button
          className="md:hidden text-brand-text p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-brand-bg/95 backdrop-blur-xl border-b border-brand-border/30"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col px-6 py-4 gap-3">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link)}
                  data-testid={`mobile-nav-${link.isRoute ? link.href.slice(1) : link.href.slice(1)}`}
                  className={`text-base font-sans py-2 transition-colors ${
                    link.label === "Book Now"
                      ? "bg-brand-gold text-black px-5 py-3 font-semibold text-center mt-2"
                      : "text-brand-text-muted hover:text-brand-gold"
                  }`}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
